import React, { createContext, useContext, useEffect, useState } from "react";
import { PageWrapper, DocumentContainer } from "../containers";
import { Navigate, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { PAGE_URL } from "../utils";
import { IOutletContext } from "../navigation/PrivateLayout";
import { DocumentData, VersionInfo } from "../interfaces/document";
import { useDocumentDetails } from "../hooks";

type PdfState = { totalPages: number };

type DocumentReviewContextType = {
  documentReview: DocumentData;
  pdfData: PdfState;
  setPdfData: React.Dispatch<React.SetStateAction<PdfState>>;
  sections: any[];
  placeholderMapping: any;
  incomingMessage: any;
  selectedVersion: string;
  availableVersions: VersionInfo[];
  switchVersion: (version: string) => Promise<void>;
  refreshSections: () => Promise<void>;
};

export const DocumentReviewContext = createContext<DocumentReviewContextType>({
  documentReview: null as unknown as DocumentData,
  pdfData: { totalPages: 0 },
  setPdfData: () => {},
  sections: [],
  placeholderMapping: {},
  incomingMessage: null,
  selectedVersion: "",
  availableVersions: [],
  switchVersion: async () => {},
  refreshSections: async () => {},
});

export const useDocumentReview = () => useContext(DocumentReviewContext);

export const Document = () => {
  const { documentUuid } = useParams();
  const navigate = useNavigate();
  const { setPageHeader, resetPageHeader } = useOutletContext<IOutletContext>();
  const [pdfData, setPdfData] = useState<PdfState>({ totalPages: 0 });

  if (!documentUuid) return <Navigate to={PAGE_URL.DOCUMENTS} replace />;

  const {
    isLoading,
    currentData,
    selectedVersion,
    availableVersions,
    switchVersion,
    refreshSections,
  } = useDocumentDetails(documentUuid);

  useEffect(() => {
    if (currentData?.document) {
      setPageHeader({
        pageRoute: `${PAGE_URL.DOCUMENT}/${currentData.document.documentUuid}`,
        pageTitle: currentData.document.fileName,
        displayBackButton: true,
        versionInfo: {
          selectedVersion,
          availableVersions,
          switchVersion,
        },
      });
    }
    if (currentData === null && !isLoading) {
      navigate(PAGE_URL.DOCUMENTS);
    }

    return () => resetPageHeader();
  }, [currentData, isLoading, selectedVersion, availableVersions]);

  return (
    <PageWrapper
      loading={isLoading}
      containerClassName="no-padding"
      pageContainerChildren={
        currentData ? (
          <DocumentReviewContext.Provider
            value={{
              documentReview: currentData.document,
              setPdfData,
              pdfData,
              sections: currentData.sections,
              placeholderMapping: currentData.placeholderMapping,
              incomingMessage: currentData.incomingMessage,
              selectedVersion,
              availableVersions,
              switchVersion,
              refreshSections,
            }}
          >
            <DocumentContainer />
          </DocumentReviewContext.Provider>
        ) : <></>
      }
    />
  );
};

export default Document;
